import { CommonModule } from "@angular/common";
import { Component, computed, inject, signal } from "@angular/core";
import { Router, RouterModule } from "@angular/router";
import { TranslateModule } from "@ngx-translate/core";
import { QuizService } from "src/app/services/api/quiz.service";
import { Quiz, QuizAttempt } from "src/app/services/model/quiz.model";
import { ToastService } from "src/app/services/toast-service/toast.service";
import { SharedModule } from "src/shared/shared.module";
import { subjectArt } from "src/app/config/subject-art";

/**
 * Everything this student can practise.
 *
 * Two lists rather than one: quizzes they have not finished, and their history.
 * The API only returns quizzes on courses they are enrolled in, so nothing here
 * filters by course — an empty list genuinely means "nothing to do".
 */
@Component({
  selector: "app-quiz-list",
  imports: [SharedModule, CommonModule, RouterModule, TranslateModule],
  templateUrl: "./quiz-list.component.html",
  styleUrl: "./quiz-list.component.scss",
})
export class QuizListComponent {
  #quizService = inject(QuizService);
  #toastService = inject(ToastService);
  #router = inject(Router);

  isLoading = signal(true);
  quizzes = signal<Quiz[]>([]);
  attempts = signal<QuizAttempt[]>([]);

  /**
   * Course quizzes and personal drills are shown in separate bands.
   *
   * A drill is now ONE set per topic, reused: the API hands back the student's
   * existing set instead of minting another, and sitting it again deals a fresh
   * draw from the same topics. So a finished drill is not spent — it is the way
   * back to that topic, and hiding it after one sitting would throw it away.
   * (It used to be hidden, because every press left another dead duplicate
   * behind and the band filled up with them.)
   */
  /**
   * What the student has typed into the search field.
   *
   * The 2021 คลังข้อสอบ page led with a line of copy and one search box, and it
   * was right to: this grid is a wall of near-identical cards whose titles are
   * long and start with the same words, so scanning it is the slowest way to
   * find anything on it. Client-side, over the title AND the course name —
   * "which quizzes belong to that course" is the other question people ask
   * here, and it has no other answer on this screen.
   */
  search = signal("");

  #matches(q: Quiz): boolean {
    const term = this.search().trim().toLowerCase();
    if (!term) return true;
    const haystack = `${q.title ?? ""} ${q.course_name ?? ""}`.toLowerCase();
    return haystack.includes(term);
  }

  courseQuizzes = computed(() =>
    this.quizzes().filter((q) => q.kind !== "practice" && this.#matches(q)),
  );

  practiceQuizzes = computed(() =>
    this.quizzes().filter((q) => q.kind === "practice" && this.#matches(q)).slice(0, 6),
  );

  /** True when the student has searched and nothing came back — as opposed to
   *  having nothing to practise at all, which is a different message. */
  noSearchResults = computed(
    () =>
      this.search().trim().length > 0 &&
      !this.courseQuizzes().length &&
      !this.practiceQuizzes().length,
  );

  /** Whether there is anything at all, search aside. */
  hasAnyQuiz = computed(() => this.quizzes().length > 0);

  onSearch(value: string): void {
    this.search.set(value ?? "");
  }

  clearSearch(): void {
    this.search.set("");
  }

  /** Best result per quiz — a retake that went worse should not replace a
   *  better earlier score in the summary. */
  bestByQuiz = computed(() => {
    const best = new Map<number, QuizAttempt>();
    for (const attempt of this.attempts()) {
      if (attempt.status !== "submitted") continue;
      const current = best.get(attempt.quiz_id);
      if (!current || attempt.percent > current.percent) best.set(attempt.quiz_id, attempt);
    }
    return best;
  });

  ngOnInit(): void {
    this.#quizService.getQuizzes().subscribe({
      next: (res: any) => {
        this.quizzes.set(res?.data ?? []);
        this.isLoading.set(false);
      },
      error: () => this.isLoading.set(false),
    });

    this.#quizService.getAttempts({ limit: 50 }).subscribe({
      next: (res: any) => this.attempts.set(res?.data ?? []),
      error: () => this.attempts.set([]),
    });
  }

  /** The quiz's mark: its course's subject art, or the generic paper icon. */
  art(quiz: Quiz): string {
    return (
      subjectArt((quiz as any).course_name) ??
      "./assets/media/landing/subjects/clock-exam.svg"
    );
  }

  best(quiz: Quiz): QuizAttempt | undefined {
    return this.bestByQuiz().get(quiz.id);
  }

  start(quiz: Quiz): void {
    this.#quizService.startAttempt(quiz.id).subscribe({
      next: () => this.#router.navigate(["/online-class/quiz", quiz.id]),
      error: (err) =>
        this.#toastService.toastError(
          err?.error?.errors || "เริ่มทำแบบฝึกหัดไม่สำเร็จ",
        ),
    });
  }
}
